// Enhanced Audio system for 3D Curriculum with seamless cross-fading
export class LoopPlayer {
  private ctx: AudioContext;
  private bufferMap: Record<string, AudioBuffer> = {};
  private activeSources: Map<string, { source: AudioBufferSourceNode, gainNode: GainNode }> = new Map();
  private masterGainNode: GainNode;
  private analyserNode?: AnalyserNode;
  private beatDetectionData?: Uint8Array;
  private onBeatCallback?: () => void;
  private lastBeatTime = 0;
  private isInitialized = false;

  constructor() {
    this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    this.masterGainNode = this.ctx.createGain();
    this.masterGainNode.connect(this.ctx.destination);
    
    // Setup AnalyserNode for beat detection
    this.setupBeatDetection();
  }

  private setupBeatDetection() {
    this.analyserNode = this.ctx.createAnalyser();
    this.analyserNode.fftSize = 128;
    this.beatDetectionData = new Uint8Array(this.analyserNode.frequencyBinCount);
    this.masterGainNode.connect(this.analyserNode);
    
    // Start the beat detection loop
    this.detectBeat();
  }

  private detectBeat() {
    if (this.analyserNode && this.beatDetectionData) {
      this.analyserNode.getByteFrequencyData(this.beatDetectionData);
      
      // Simple bass drum detection (low frequencies)
      const bassFrequencies = this.beatDetectionData.slice(0, 5);
      const averageBass = bassFrequencies.reduce((sum, value) => sum + value, 0) / bassFrequencies.length;

      // Threshold and cooldown to detect a beat
      const beatThreshold = 200; // Sensitivity
      const cooldown = 300; // ms (to prevent multiple triggers for one beat)
      const now = Date.now();

      if (averageBass > beatThreshold && now - this.lastBeatTime > cooldown) {
        this.lastBeatTime = now;
        if (this.onBeatCallback) {
          this.onBeatCallback();
        }
      }
    }
    requestAnimationFrame(() => this.detectBeat());
  }

  setOnBeatCallback(callback: () => void) {
    this.onBeatCallback = callback;
  }

  // Initialize audio context on first user interaction
  async initialize() {
    if (this.isInitialized) return;
    
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume();
    }
    this.isInitialized = true;
  }

  // Create simple synthetic audio buffer
  private createSynthAudio(frequency: number, duration: number, type: OscillatorType = 'sine'): AudioBuffer {
    const sampleRate = this.ctx.sampleRate;
    const frameCount = sampleRate * duration;
    const buffer = this.ctx.createBuffer(1, frameCount, sampleRate);
    const channelData = buffer.getChannelData(0);

    for (let i = 0; i < frameCount; i++) {
      const t = i / sampleRate;
      let sample = 0;
      
      switch (type) {
        case 'sine':
          sample = Math.sin(2 * Math.PI * frequency * t);
          break;
        case 'triangle':
          sample = (2 / Math.PI) * Math.asin(Math.sin(2 * Math.PI * frequency * t));
          break;
        case 'sawtooth':
          sample = 2 * (t * frequency - Math.floor(t * frequency + 0.5));
          break;
        case 'square':
          sample = Math.sin(2 * Math.PI * frequency * t) > 0 ? 1 : -1;
          break;
      }
      
      // Apply envelope (fade in/out)
      const envelope = Math.min(t * 10, 1) * Math.min((duration - t) * 10, 1);
      channelData[i] = sample * envelope * 0.3;
    }

    return buffer;
  }

  // Create rich ambient loop buffer with harmonics and textures
  private createAmbientLoop(baseFreq: number, worldType: string, duration: number = 8): AudioBuffer {
    const sampleRate = this.ctx.sampleRate;
    const frameCount = sampleRate * duration;
    const buffer = this.ctx.createBuffer(2, frameCount, sampleRate);
    
    for (let channel = 0; channel < 2; channel++) {
      const channelData = buffer.getChannelData(channel);
      const panOffset = channel === 0 ? -0.1 : 0.1; // Subtle stereo spread
      
      for (let i = 0; i < frameCount; i++) {
        const t = i / sampleRate;
        
        // World-specific ambient characteristics
        let sample = 0;
        
        switch (worldType) {
          case 'rhythm':
            // Percussive, pulsing ambient
            const rhythmPulse = Math.sin(2 * Math.PI * 0.5 * t) * 0.5 + 0.5; // 120 BPM pulse
            sample = Math.sin(2 * Math.PI * baseFreq * t) * 0.3 * rhythmPulse;
            sample += Math.sin(2 * Math.PI * baseFreq * 2 * t) * 0.1;
            break;
            
          case 'harmony':
            // Rich harmonic content
            sample = Math.sin(2 * Math.PI * baseFreq * t) * 0.25;
            sample += Math.sin(2 * Math.PI * baseFreq * 1.5 * t) * 0.2; // Perfect fifth
            sample += Math.sin(2 * Math.PI * baseFreq * 1.25 * t) * 0.15; // Major third
            sample += Math.sin(2 * Math.PI * baseFreq * 2 * t) * 0.1; // Octave
            break;
            
          case 'instrument':
            // Textured, timbre-rich
            const sawWave = 2 * (t * baseFreq - Math.floor(t * baseFreq + 0.5));
            const filter = Math.sin(2 * Math.PI * 0.1 * t) * 0.5 + 0.5;
            sample = sawWave * 0.2 * filter;
            sample += Math.sin(2 * Math.PI * baseFreq * t) * 0.15;
            break;
            
          case 'melody':
            // Flowing, melodic ambient
            const melodicPhase = Math.sin(2 * Math.PI * 0.05 * t) * 2;
            sample = Math.sin(2 * Math.PI * (baseFreq + melodicPhase) * t) * 0.25;
            sample += Math.sin(2 * Math.PI * baseFreq * 0.5 * t) * 0.2;
            break;
            
          case 'form':
            // Structured, architectural
            const formPattern = Math.floor(t * 2) % 4 < 2 ? 1 : 0.5;
            sample = Math.sin(2 * Math.PI * baseFreq * t) * 0.2 * formPattern;
            sample += Math.sin(2 * Math.PI * baseFreq * 1.33 * t) * 0.1;
            break;
            
          case 'dynamics':
            // Volume-based, crescendo/diminuendo
            const dynamicEnv = Math.sin(2 * Math.PI * 0.125 * t) * 0.4 + 0.6;
            sample = Math.sin(2 * Math.PI * baseFreq * t) * 0.3 * dynamicEnv;
            sample += Math.sin(2 * Math.PI * baseFreq * 0.75 * t) * 0.15;
            break;
            
          case 'showcase':
            // Triumphant, celebratory
            sample = Math.sin(2 * Math.PI * baseFreq * t) * 0.3;
            sample += Math.sin(2 * Math.PI * baseFreq * 1.5 * t) * 0.2;
            sample += Math.sin(2 * Math.PI * baseFreq * 2 * t) * 0.15;
            const sparkle = Math.random() * 0.05 * (Math.sin(2 * Math.PI * 3 * t) > 0.8 ? 1 : 0);
            sample += sparkle;
            break;
            
          default:
            sample = Math.sin(2 * Math.PI * baseFreq * t) * 0.25;
        }
        
        // Add subtle noise and modulation
        const lowFreqMod = Math.sin(2 * Math.PI * 0.03 * t + panOffset) * 0.1 + 0.9;
        const noise = (Math.random() - 0.5) * 0.02;
        
        channelData[i] = (sample + noise) * lowFreqMod * 0.4;
      }
    }

    return buffer;
  }

  // Load audio file and decode, with enhanced synthetic fallback
  async load(name: string, url: string): Promise<void> {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const arrayBuffer = await response.arrayBuffer();
      if (arrayBuffer.byteLength === 0) throw new Error('Empty audio file');
      
      this.bufferMap[name] = await this.ctx.decodeAudioData(arrayBuffer);
    } catch (error) {
      console.warn(`Failed to load audio: ${url}, creating enhanced synthetic audio`, error);
      
      // Create synthetic audio based on name
      if (name.includes('ambient')) {
        // Enhanced ambient loops with world-specific characteristics
        const frequencies = {
          'rhythm': 110,    // A2
          'harmony': 146.83, // D3  
          'instrument': 174.61, // F3
          'melody': 220,    // A3
          'form': 261.63,   // C4
          'dynamics': 293.66, // D4
          'showcase': 329.63  // E4
        };
        const worldType = name.replace('ambient-', '').replace('.mp3', '');
        const freq = frequencies[worldType as keyof typeof frequencies] || 220;
        this.bufferMap[name] = this.createAmbientLoop(freq, worldType);
      } else {
        // Create simple UI sounds
        const soundConfigs = {
          'ping-hover': { freq: 800, duration: 0.2, type: 'sine' as OscillatorType },
          'click-arrow': { freq: 600, duration: 0.15, type: 'triangle' as OscillatorType },
          'tick-key': { freq: 1000, duration: 0.1, type: 'square' as OscillatorType },
          'jingle-select': { freq: 523.25, duration: 0.5, type: 'sine' as OscillatorType },
          'swipe-sfx': { freq: 400, duration: 0.3, type: 'sawtooth' as OscillatorType },
          'page-open': { freq: 440, duration: 0.4, type: 'triangle' as OscillatorType },
          'page-close': { freq: 330, duration: 0.4, type: 'triangle' as OscillatorType },
          'sparkle': { freq: 1047, duration: 0.6, type: 'sine' as OscillatorType },
          'fireworks': { freq: 220, duration: 1.0, type: 'sawtooth' as OscillatorType },
          'badge-drop': { freq: 659.25, duration: 0.8, type: 'sine' as OscillatorType },
          'triumph': { freq: 261.63, duration: 1.5, type: 'triangle' as OscillatorType }
        };
        
        const config = soundConfigs[name.replace('.mp3', '') as keyof typeof soundConfigs] || 
                      { freq: 440, duration: 0.3, type: 'sine' as OscillatorType };
        
        this.bufferMap[name] = this.createSynthAudio(config.freq, config.duration, config.type);
      }
    }
  }

  // Start a loop with individual gain control
  private startLoop(name: string, volume = 0.2): { source: AudioBufferSourceNode, gainNode: GainNode } | null {
    if (!this.bufferMap[name] || !this.isInitialized) return null;

    const source = this.ctx.createBufferSource();
    const gainNode = this.ctx.createGain();
    
    source.buffer = this.bufferMap[name];
    source.loop = true;
    
    gainNode.gain.setValueAtTime(0, this.ctx.currentTime); // Start silent
    
    source.connect(gainNode);
    gainNode.connect(this.masterGainNode);
    source.start();
    
    return { source, gainNode };
  }

  // Seamless cross-fade to new loop
  async crossFadeToLoop(name: string, targetVolume = 0.2, fadeTime = 0.8): Promise<void> {
    if (!this.bufferMap[name] || !this.isInitialized) return;

    const currentTime = this.ctx.currentTime;
    
    // Fade out all currently playing tracks
    this.activeSources.forEach(({ gainNode }) => {
      gainNode.gain.linearRampToValueAtTime(0, currentTime + fadeTime);
    });

    // Start new loop
    const newLoop = this.startLoop(name, targetVolume);
    if (!newLoop) return;

    // Fade in new track
    newLoop.gainNode.gain.linearRampToValueAtTime(targetVolume, currentTime + fadeTime);
    
    // Store new active source
    this.activeSources.set(name, newLoop);
    
    // Clean up old sources after fade
    setTimeout(() => {
      this.activeSources.forEach((sourceData, key) => {
        if (key !== name) {
          sourceData.source.stop();
          this.activeSources.delete(key);
        }
      });
    }, fadeTime * 1000 + 100);
  }

  // Quick cross-fade for rapid planet switching
  async quickCrossFade(name: string, targetVolume = 0.25, fadeTime = 0.3): Promise<void> {
    return this.crossFadeToLoop(name, targetVolume, fadeTime);
  }

  // Legacy method for compatibility
  async fadeToLoop(name: string, volume = 0.2, fadeTime = 1.0): Promise<void> {
    return this.crossFadeToLoop(name, volume, fadeTime);
  }

  // Play looping ambient track (legacy compatibility)
  playLoop(name: string, volume = 0.2): AudioBufferSourceNode | null {
    this.crossFadeToLoop(name, volume, 0.1);
    return this.activeSources.get(name)?.source || null;
  }

  // Stop all loops
  stopAll(): void {
    this.activeSources.forEach(({ source }) => {
      source.stop();
    });
    this.activeSources.clear();
  }

  // Stop current loop (legacy compatibility)
  stop(): void {
    this.stopAll();
  }

  // Set master volume for all tracks
  setMasterVolume(volume: number): void {
    this.masterGainNode.gain.setValueAtTime(volume, this.ctx.currentTime);
  }

  // Set volume (legacy compatibility)
  setVolume(volume: number): void {
    this.setMasterVolume(volume);
  }

  // Get current playing track name
  getCurrentTrack(): string | null {
    return this.activeSources.size > 0 ? Array.from(this.activeSources.keys())[0] : null;
  }
}

// Simple sound effect player with enhanced synthetic fallback
export function playSound(url: string, volume = 0.5): void {
  try {
    const audio = new Audio();
    
    // Set up error handling before setting src
    audio.addEventListener('error', () => {
      console.warn(`Failed to play sound: ${url}, using synthetic fallback`);
      playSyntheticSound(url, volume);
    });
    
    audio.volume = Math.max(0, Math.min(1, volume));
    audio.src = url;
    
    audio.play().catch(() => {
      // Fallback to synthetic audio if play fails
      console.warn(`Failed to play sound: ${url}, using synthetic fallback`);
      playSyntheticSound(url, volume);
    });
  } catch (error) {
    console.warn(`Failed to create audio: ${url}, using synthetic fallback`);
    playSyntheticSound(url, volume);
  }
}

// Synthetic audio fallback
function playSyntheticSound(url: string, volume: number): void {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    // Configure based on filename
    const filename = url.split('/').pop() || '';
    let frequency = 440;
    let duration = 0.3;
    let type: OscillatorType = 'sine';
    
    if (filename.includes('ping')) { frequency = 800; duration = 0.2; }
    else if (filename.includes('click')) { frequency = 600; duration = 0.15; type = 'triangle'; }
    else if (filename.includes('tick')) { frequency = 1000; duration = 0.1; type = 'square'; }
    else if (filename.includes('jingle')) { frequency = 523.25; duration = 0.5; }
    else if (filename.includes('swipe')) { frequency = 400; duration = 0.3; type = 'sawtooth'; }
    else if (filename.includes('page')) { frequency = 440; duration = 0.4; type = 'triangle'; }
    
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
    
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(volume * 0.3, audioContext.currentTime + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + duration);
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.start();
    oscillator.stop(audioContext.currentTime + duration);
  } catch (error) {
    console.warn('Synthetic audio fallback failed:', error);
  }
}

// Enhanced audio manager for the entire curriculum with seamless transitions
export class CurriculumAudioManager {
  private loopPlayer: LoopPlayer;
  private isEnabled = true;
  private masterVolume = 0.7;
  private currentWorldIndex = -1;
  private transitionSpeed: 'quick' | 'normal' | 'slow' = 'normal';

  // World-specific ambient tracks with enhanced characteristics
  private worldAmbientTracks = {
    0: 'ambient-rhythm', // Rhythm Realm - Percussive, pulsing
    1: 'ambient-harmony', // Harmony Heights - Rich harmonics  
    2: 'ambient-instrument', // Instrument Island - Textured timbres
    3: 'ambient-melody', // Melody Mountain - Flowing melodies
    4: 'ambient-form', // Form Fields - Structured patterns
    5: 'ambient-dynamics', // Dynamics Dome - Volume variations
    6: 'ambient-showcase' // Final Showcase - Triumphant celebration
  };

  constructor() {
    this.loopPlayer = new LoopPlayer();
  }

  // Pass a callback to be triggered on each detected beat
  setOnBeatCallback(callback: () => void) {
    this.loopPlayer.setOnBeatCallback(callback);
  }

  async initialize(): Promise<void> {
    await this.loopPlayer.initialize();
    
    // Load all ambient tracks
    const loadPromises = [
      this.loopPlayer.load('ambient-rhythm', '/audio/ambient-rhythm.mp3'),
      this.loopPlayer.load('ambient-harmony', '/audio/ambient-harmony.mp3'),
      this.loopPlayer.load('ambient-instrument', '/audio/ambient-instrument.mp3'),
      this.loopPlayer.load('ambient-melody', '/audio/ambient-melody.mp3'),
      this.loopPlayer.load('ambient-form', '/audio/ambient-form.mp3'),
      this.loopPlayer.load('ambient-dynamics', '/audio/ambient-dynamics.mp3'),
      this.loopPlayer.load('ambient-showcase', '/audio/ambient-showcase.mp3')
    ];

    await Promise.all(loadPromises);
  }

  // Set transition speed for planet switching
  setTransitionSpeed(speed: 'quick' | 'normal' | 'slow'): void {
    this.transitionSpeed = speed;
  }

  // Get fade time based on transition speed
  private getFadeTime(): number {
    switch (this.transitionSpeed) {
      case 'quick': return 0.3;
      case 'slow': return 2.0;
      default: return 0.8;
    }
  }

  // Get target volume based on transition speed (louder for quicker transitions)
  private getTargetVolume(): number {
    const baseVolume = 0.25 * this.masterVolume;
    switch (this.transitionSpeed) {
      case 'quick': return baseVolume * 1.2; // 20% louder for quick transitions
      case 'slow': return baseVolume * 0.9;  // 10% quieter for slow transitions
      default: return baseVolume;
    }
  }

  // Switch ambient track for world with enhanced morphing
  switchToWorld(worldIndex: number): void {
    if (!this.isEnabled || worldIndex === this.currentWorldIndex) return;
    
    const trackName = this.worldAmbientTracks[worldIndex as keyof typeof this.worldAmbientTracks];
    if (trackName) {
      const fadeTime = this.getFadeTime();
      const targetVolume = this.getTargetVolume();
      
      if (this.transitionSpeed === 'quick') {
        // Use quick cross-fade for rapid planet switching
        this.loopPlayer.quickCrossFade(trackName, targetVolume, fadeTime);
      } else {
        // Use standard cross-fade for normal transitions
        this.loopPlayer.crossFadeToLoop(trackName, targetVolume, fadeTime);
      }
      
      this.currentWorldIndex = worldIndex;
      console.log(`🎵 Morphed to ${trackName} (fade: ${fadeTime}s, volume: ${targetVolume.toFixed(2)})`);
    }
  }

  // Enhanced world switching with automatic speed adjustment
  switchToWorldSmooth(worldIndex: number, isQuickNavigation = false): void {
    const previousSpeed: 'quick' | 'normal' | 'slow' = this.transitionSpeed;
    
    // Temporarily adjust speed based on navigation type
    if (isQuickNavigation) {
      this.setTransitionSpeed('quick');
    }
    
    this.switchToWorld(worldIndex);
    
    // Restore previous speed after a delay
    setTimeout(() => {
      this.setTransitionSpeed(previousSpeed);
    }, 1000);
  }

  // Play UI sound effects
  playHoverSound(): void {
    if (this.isEnabled) {
    }
  }

  playSelectSound(): void {
    if (this.isEnabled) {
    }
  }

  playArrowClick(): void {
    if (this.isEnabled) {
      // Use quick transition for arrow navigation
      this.setTransitionSpeed('quick');
    }
  }

  playKeyNavigation(): void {
    if (this.isEnabled) {
      // Use quick transition for keyboard navigation
      this.setTransitionSpeed('quick');
    }
  }

  playSwipeGesture(): void {
    if (this.isEnabled) {
      playSound('/audio/swipe-sfx.mp3', 0.2 * this.masterVolume);
      // Use quick transition for swipe gestures
      this.setTransitionSpeed('quick');
    }
  }

  playModalOpen(): void {
    if (this.isEnabled) {
      playSound('/audio/page-open.mp3', 0.3 * this.masterVolume);
    }
  }

  playModalClose(): void {
    if (this.isEnabled) {
      playSound('/audio/page-close.mp3', 0.3 * this.masterVolume);
    }
  }

  // Achievement sounds with ambient enhancement
  playLessonComplete(): void {
    if (this.isEnabled) {
      playSound('/audio/jingle-select.mp3', 0.4 * this.masterVolume);
      // Briefly boost ambient volume for celebration
      this.boostAmbientTemporarily(1.3, 2000);
    }
  }

  playWorldComplete(): void {
    if (this.isEnabled) {
      playSound('/audio/fireworks.mp3', 0.6 * this.masterVolume);
      // Significantly boost ambient for world completion
      this.boostAmbientTemporarily(1.5, 3000);
    }
  }

  playBadgeEarned(): void {
    if (this.isEnabled) {
      playSound('/audio/badge-drop.mp3', 0.4 * this.masterVolume);
      this.boostAmbientTemporarily(1.2, 1500);
    }
  }

  playTriumph(): void {
    if (this.isEnabled) {
      playSound('/audio/triumph.mp3', 0.7 * this.masterVolume);
      this.boostAmbientTemporarily(1.4, 4000);
    }
  }

  // Temporarily boost ambient volume for celebrations
  private boostAmbientTemporarily(multiplier: number, duration: number): void {
    const originalVolume = this.loopPlayer.getCurrentTrack() ? this.getTargetVolume() : 0;
    const boostedVolume = originalVolume * multiplier;
    
    // Boost volume
    this.loopPlayer.setMasterVolume(boostedVolume);
    
    // Return to normal after duration
    setTimeout(() => {
      this.loopPlayer.setMasterVolume(originalVolume);
    }, duration);
  }

  // Audio controls
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    if (!enabled) {
      this.loopPlayer.stopAll();
    }
  }

  setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    const currentVolume = this.getTargetVolume();
    this.loopPlayer.setMasterVolume(currentVolume);
  }

  // Stop all audio immediately
  stop(): void {
    if (this.loopPlayer) {
      this.loopPlayer.stopAll();
      this.currentWorldIndex = -1; // Reset the current world index
      console.log('⏹️ Ambient audio stopped.');
    }
  }

  // Toggle all audio on or off
  toggleAudio(force?: boolean): boolean {
    this.isEnabled = force !== undefined ? force : !this.isEnabled;
    console.log(`🔊 Audio ${this.isEnabled ? 'enabled' : 'disabled'}`);
    
    if (!this.isEnabled) {
      this.stop();
    } else if (this.currentWorldIndex !== -1) {
      // If audio is re-enabled, resume the ambient track for the current world
      this.switchToWorld(this.currentWorldIndex);
    }
    
    return this.isEnabled;
  }

  // Get current state
  getCurrentWorld(): number {
    return this.currentWorldIndex;
  }

  getCurrentTrack(): string | null {
    return this.loopPlayer.getCurrentTrack();
  }

  getTransitionSpeed(): 'quick' | 'normal' | 'slow' {
    return this.transitionSpeed;
  }
}

// Global audio manager instance
export const audioManager = new CurriculumAudioManager();

// Initialize audio on first user interaction
let audioInitialized = false;
export const initializeAudio = async (): Promise<void> => {
  if (audioInitialized) return;
  
  try {
    await audioManager.initialize();
    audioInitialized = true;
    console.log('Audio system initialized');
  } catch (error) {
    console.warn('Failed to initialize audio system:', error);
  }
};

// Auto-initialize on first click/touch
if (typeof window !== 'undefined') {
  const initOnInteraction = () => {
    initializeAudio();
    window.removeEventListener('click', initOnInteraction);
    window.removeEventListener('touchstart', initOnInteraction);
  };
  
  window.addEventListener('click', initOnInteraction, { once: true });
  window.addEventListener('touchstart', initOnInteraction, { once: true });
} 